import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import ScrollProgress from "@/components/feature/ScrollProgress";
import Reveal from "@/components/base/Reveal";
import { useSiteContent } from "@/context/SiteContentContext";
import { supabase } from "@/lib/supabase";
import type { ProjectImage } from "@/lib/types";

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { projects, loading, error, refresh } = useSiteContent();
  const [gallery, setGallery] = useState<ProjectImage[]>([]);

  const project = projects.find((p) => (p.slug || p.id) === slug);

  useEffect(() => {
    if (project?.id) {
      supabase
        .from("project_images")
        .select("*")
        .eq("project_id", project.id)
        .order("orden")
        .then(({ data }) => setGallery((data ?? []) as ProjectImage[]));
    }
  }, [project?.id]);

  const handleSolicitar = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" });
    }, 120);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-50">
        <ScrollProgress />
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-20 text-center">
          <p className="text-foreground-600 mb-8">{error}</p>
          <button
            onClick={refresh}
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 text-background-50 px-6 py-3 text-sm font-semibold hover:bg-primary-600 transition-all"
          >
            <i className="ri-refresh-line" />
            Reintentar
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background-50">
        <ScrollProgress />
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 pt-40 pb-20 text-center">
          <h1 className="text-3xl font-bold text-foreground-950 mb-4">Proyecto no encontrado</h1>
          <p className="text-foreground-600 mb-8">
            El proyecto que buscas no existe o aún no está disponible.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 text-background-50 px-6 py-3 text-sm font-semibold hover:bg-primary-600 transition-all"
          >
            <i className="ri-arrow-left-line" />
            Volver al inicio
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const fullDesc = project.descripcion_completa || project.descripcion || "";
  const caracteristicas = project.caracteristicas ?? [];
  const tecnologias = project.tecnologias ?? [];

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollProgress />
      <Navbar />

      <main>
        <section className="relative pt-28 md:pt-32 pb-12 overflow-hidden">
          <div className="absolute inset-0 digital-grid opacity-50" />
          <div className="absolute -top-20 right-0 w-96 h-96 rounded-full bg-primary-500/10 blur-3xl" />
          <div className="relative max-w-5xl mx-auto px-4 md:px-8">
            <Reveal>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-colors mb-6"
              >
                <i className="ri-arrow-left-line" />
                Volver a proyectos
              </Link>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {project.categoria && (
                  <span className="rounded-full bg-primary-100 text-primary-700 px-3 py-1 text-xs font-semibold">
                    {project.categoria}
                  </span>
                )}
                <span className="text-sm text-foreground-500">
                  {[project.cliente, project.anio].filter(Boolean).join(" · ")}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
                {project.nombre}
              </h1>
            </Reveal>
          </div>
        </section>

        {project.imagen && (
          <section className="max-w-5xl mx-auto px-4 md:px-8">
            <Reveal variant="scale">
              <div className="rounded-3xl overflow-hidden border border-background-200 shadow-[0_20px_60px_rgba(30,60,120,0.12)]">
                <img
                  src={project.imagen}
                  alt={`${project.nombre} - vista principal`}
                  title={`${project.nombre} - ${project.categoria || ""}`}
                  className="w-full aspect-[16/9] object-cover object-top"
                />
              </div>
            </Reveal>
          </section>
        )}

        <section className="max-w-5xl mx-auto px-4 md:px-8 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Reveal className="lg:col-span-2">
              <h2 className="text-2xl font-semibold text-foreground-950 mb-4">Sobre el proyecto</h2>
              <p className="text-foreground-600 leading-relaxed">{fullDesc}</p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <h3 className="flex items-center gap-2 font-semibold text-foreground-950 mb-2">
                    <i className="ri-error-warning-line text-primary-500" />
                    Problema
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {project.problema || "—"}
                  </p>
                </div>
                <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <h3 className="flex items-center gap-2 font-semibold text-foreground-950 mb-2">
                    <i className="ri-lightbulb-line text-accent-500" />
                    Solución
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {project.solucion || "—"}
                  </p>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-5">
                <h3 className="flex items-center gap-2 font-semibold text-foreground-950 mb-2">
                  <i className="ri-line-chart-line text-primary-500" />
                  Resultado
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {project.resultado || "—"}
                </p>
              </div>
            </Reveal>

            <Reveal variant="right">
              <div className="space-y-5">
                {caracteristicas.length > 0 && (
                  <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
                    <h3 className="font-semibold text-foreground-950 mb-4">
                      Características principales
                    </h3>
                    <ul className="space-y-2.5">
                      {caracteristicas.map((c) => (
                        <li key={c} className="flex items-start gap-2.5 text-sm text-foreground-700">
                          <i className="ri-checkbox-circle-line text-primary-500 mt-0.5" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {tecnologias.length > 0 && (
                  <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
                    <h3 className="font-semibold text-foreground-950 mb-4">Tecnologías</h3>
                    <div className="flex flex-wrap gap-2">
                      {tecnologias.map((tech) => (
                        <span
                          key={tech}
                          className="rounded-full bg-secondary-100 text-secondary-800 px-3 py-1 text-xs font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  {project.url && (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3.5 text-sm font-semibold transition-all whitespace-nowrap"
                    >
                      Ver sitio en vivo
                      <i className="ri-external-link-line" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={handleSolicitar}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-primary-300 text-primary-700 hover:bg-primary-50 px-6 py-3.5 text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    Solicitar un proyecto similar
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {gallery.length > 0 && (
          <section className="max-w-5xl mx-auto px-4 md:px-8 pb-16">
            <Reveal>
              <h2 className="text-2xl font-semibold text-foreground-950 mb-6">Galería</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {gallery.map((img, i) => (
                <Reveal key={img.id} delay={i * 80}>
                  <div className="rounded-2xl overflow-hidden border border-background-200">
                    <img
                      src={img.url}
                      alt={img.alt || `${project.nombre} - imagen ${i + 1}`}
                      title={`${project.nombre} - galería ${i + 1}`}
                      className="w-full aspect-[16/10] object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}